import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type SitesUpdate = Database['public']['Tables']['sites']['Update'];
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Upload, Trash2, Loader2, Image, Sparkles, Check, MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SiteAd {
  id: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  click_count: number;
}

interface SiteAdManagerProps {
  siteId: string;
  adType: 'seller' | 'buyer' | 'market_value' | 'mortgage' | 'community_guide';
  photoUrl?: string | null;
  logoUrl?: string | null;
  onActiveImageChange: (url: string | null) => void;
}

const adTypeLabels: Record<string, string> = {
  seller: 'Seller',
  buyer: 'Buyer',
  market_value: 'Market Value Report',
  mortgage: 'Mortgage Info',
  community_guide: 'Community Guide',
};

export function SiteAdManager({
  siteId,
  adType,
  photoUrl,
  logoUrl,
  onActiveImageChange,
}: SiteAdManagerProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ads, setAds] = useState<SiteAd[]>([]);
  const [loadingAds, setLoadingAds] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch existing ads for this site and type
  const fetchAds = async () => {
    if (!siteId) {
      setLoadingAds(false);
      return;
    }
    
    setLoadingAds(true);
    const { data, error } = await supabase
      .from('site_ads')
      .select('*')
      .eq('site_id', siteId)
      .eq('ad_type', adType)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching ads:', error);
      setAds([]);
    } else {
      setAds(data || []);
      // Update the parent with the first active image (for legacy compatibility)
      const activeAd = data?.find(ad => ad.is_active);
      if (activeAd) {
        onActiveImageChange(activeAd.image_url);
      }
    }
    setLoadingAds(false);
  };

  useEffect(() => {
    if (siteId) {
      fetchAds();
    }
  }, [siteId, adType]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, WebP)',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Image must be under 2MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `site-ads/${siteId}/${adType}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('realtor-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('realtor-assets')
        .getPublicUrl(fileName);

      // Check if we're at the limit
      if (ads.length >= 3) {
        // Delete the oldest ad
        const oldestAd = ads[ads.length - 1];
        await supabase
          .from('site_ads')
          .delete()
          .eq('id', oldestAd.id);
      }

      // Insert new ad - active by default if first, otherwise inactive
      const isFirst = ads.length === 0;
      await supabase
        .from('site_ads')
        .insert({
          site_id: siteId,
          ad_type: adType,
          image_url: publicUrl,
          is_active: isFirst,
        });

      if (isFirst) {
        onActiveImageChange(publicUrl);
        // Update legacy field
        const legacyField = adType === 'seller' ? 'seller_ad_image_url' 
          : adType === 'buyer' ? 'buyer_ad_image_url' 
          : adType === 'market_value' ? 'seller_ad_image_url'
          : 'mortgage_ad_image_url';
        await supabase
          .from('sites')
          .update({ [legacyField]: publicUrl } as SitesUpdate)
          .eq('id', siteId);
      }

      await fetchAds();

      toast({
        title: 'Image uploaded',
        description: 'Ad image has been added.',
      });
    } catch (error) {
      console.error('Error uploading ad image:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = async (adId: string) => {
    const adToRemove = ads.find(a => a.id === adId);
    if (!adToRemove) return;

    const { error } = await supabase
      .from('site_ads')
      .delete()
      .eq('id', adId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove ad.',
        variant: 'destructive',
      });
      return;
    }

    // If we removed an active ad and there are no more active ones, update legacy field
    const remainingAds = ads.filter(a => a.id !== adId);
    const hasActiveAd = remainingAds.some(a => a.is_active);
    
    if (!hasActiveAd) {
      onActiveImageChange(null);
      // Clear legacy field
      const legacyField = adType === 'seller' ? 'seller_ad_image_url' 
        : adType === 'buyer' ? 'buyer_ad_image_url' 
        : adType === 'market_value' ? 'seller_ad_image_url'
        : 'mortgage_ad_image_url';
      await supabase
        .from('sites')
        .update({ [legacyField]: null } as SitesUpdate)
        .eq('id', siteId);
    }

    await fetchAds();
    toast({
      title: 'Ad removed',
      description: 'The ad image has been removed.',
    });
  };

  // Toggle active status (can have multiple active)
  const handleToggleActive = async (adId: string) => {
    const adToToggle = ads.find(a => a.id === adId);
    if (!adToToggle) return;

    const newActiveStatus = !adToToggle.is_active;

    await supabase
      .from('site_ads')
      .update({ is_active: newActiveStatus })
      .eq('id', adId);

    // Update legacy field with first active ad (or null if none)
    const updatedAds = ads.map(a => 
      a.id === adId ? { ...a, is_active: newActiveStatus } : a
    );
    const firstActiveAd = updatedAds.find(a => a.is_active);
    
    const legacyField = adType === 'seller' ? 'seller_ad_image_url' 
      : adType === 'buyer' ? 'buyer_ad_image_url' 
      : adType === 'market_value' ? 'seller_ad_image_url'
      : 'mortgage_ad_image_url';
    await supabase
      .from('sites')
      .update({ [legacyField]: firstActiveAd?.image_url || null } as SitesUpdate)
      .eq('id', siteId);

    onActiveImageChange(firstActiveAd?.image_url || null);
    await fetchAds();

    toast({
      title: newActiveStatus ? 'Ad activated' : 'Ad deactivated',
      description: newActiveStatus 
        ? 'This ad will now be included in rotation.' 
        : 'This ad will no longer be shown.',
    });
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('generate-site-ads', {
        body: { siteId, adType, photoUrl, logoUrl },
      });

      if (response.error) {
        throw response.error;
      }

      const data = response.data;

      if (data.success && data.image_url) {
        await fetchAds();
        // Only update parent if it was the first ad
        if (ads.length === 0) {
          onActiveImageChange(data.image_url);
        }
        toast({
          title: 'Ad generated',
          description: `AI-generated ${adTypeLabels[adType]} ad has been created.`,
        });
      } else {
        throw new Error(data.error || 'No image returned');
      }
    } catch (error) {
      console.error('Error generating ad:', error);
      toast({
        title: 'Generation failed',
        description: error instanceof Error ? error.message : 'Failed to generate ad. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const isLoading = uploading || generating;
  const canAddMore = ads.length < 3;
  const activeCount = ads.filter(a => a.is_active).length;

  return (
    <div className="space-y-4">
      {/* Gallery of up to 3 ads */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Ad Images ({ads.length}/3)</h4>
          {ads.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {activeCount} active • Click to toggle
            </span>
          )}
        </div>

        {loadingAds ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : ads.length === 0 ? (
          <div className="w-full max-w-md aspect-square border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
            <div className="text-center p-4">
              <Image className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <span className="text-sm text-muted-foreground">No ad images<br />1080×1080px</span>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {ads.map((ad) => (
              <div
                key={ad.id}
                className={cn(
                  "relative aspect-square border-2 rounded-lg overflow-hidden cursor-pointer transition-all",
                  ad.is_active 
                    ? "border-primary ring-2 ring-primary/20" 
                    : "border-muted hover:border-primary/50 opacity-60"
                )}
                onClick={() => handleToggleActive(ad.id)}
              >
                <img
                  src={ad.image_url}
                  alt={`${adTypeLabels[adType]} ad`}
                  className="w-full h-full object-cover"
                />
                {ad.is_active && (
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Active
                  </div>
                )}
                {/* Click count badge */}
                <div className="absolute bottom-2 left-2 bg-background/90 text-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                  <MousePointerClick className="h-3 w-3" />
                  {ad.click_count || 0}
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(ad.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
          id={`site-ad-upload-${adType}`}
        />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateAI}
          disabled={isLoading || !canAddMore}
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate with AI
            </>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading || !canAddMore}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </>
          )}
        </Button>

        {!canAddMore && (
          <p className="text-xs text-muted-foreground self-center">
            Maximum 3 ads. Remove one to add more.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Recommended: 1080×1080px (1:1). Multiple active ads will rotate randomly on public pages.
      </p>
    </div>
  );
}
